import React, { useCallback, useContext, useState } from 'react'
import { ThemeContext } from 'styled-components'
import { FiMoreVertical, FiPlus } from 'react-icons/fi'
import { FaArrowsAlt } from 'react-icons/fa'
import { withStyles } from '@material-ui/core/styles'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Swal from 'sweetalert2'

import { OptionGroupProps } from 'utils/props'
import { useToast } from 'contexts/ToastContext'

import OptionModal from './OptionModal'

import * as S from './styles'

interface Props {
  optionGroup: OptionGroupProps[]
  setOptionGroup(newState: OptionGroupProps[]): void
}

const Options: React.FC<Props> = ({ optionGroup, setOptionGroup }) => {
  const { showToast } = useToast()
  const themeContext = useContext(ThemeContext)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [optionSelected, setOptionSelected] = useState(null)

  const StyledMenu = withStyles({
    paper: {
      backgroundColor: themeContext.colors.secondary
    }
  })((props) => (
    <Menu
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handleMenuOptionsClose}
      elevation={0}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center'
      }}
      {...props}
    />
  ))

  const StyledMenuItem = withStyles((theme) => ({
    root: {
      backgroundColor: themeContext.colors.secondary,
      color: themeContext.colors.text.cards,
      fontSize: '1.8rem',

      '&:hover': {
        backgroundColor: themeContext.colors.primary,
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
          color: theme.palette.common.white
        }
      }
    }
  }))(MenuItem)

  const handleToggle = useCallback((option = null) => {
    setOptionSelected(option)
    setIsOpen((prevState: boolean) => !prevState)
  }, [])

  const handleMenuOptionsClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleMenuOptionsOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, option: OptionGroupProps) => {
      setOptionSelected(option)
      setAnchorEl(event.currentTarget)
    },
    []
  )

  const handleUpdateStatusOption = useCallback(
    (id: string) => {
      const newState = optionGroup.map((option) => {
        if (option.id === id) {
          return { ...option, available: !option.available }
        }

        return option
      })

      setOptionGroup(newState)
    },
    [optionGroup, setOptionGroup]
  )

  const handleModalOptionEdit = useCallback(() => {
    handleMenuOptionsClose()
    handleToggle(optionSelected)
  }, [handleMenuOptionsClose, handleToggle, optionSelected])

  const handleOptionDelete = useCallback(() => {
    handleMenuOptionsClose()

    try {
      Swal.fire({
        title: `Isto irá excluir a opção "${optionSelected.name}". Deseja confirmar?`,
        icon: 'question',
        showCancelButton: true,
        reverseButtons: false,
        confirmButtonColor: '#50A773',
        cancelButtonColor: '#EA1D2C',
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar'
      }).then(async (result) => {
        if (result.value) {
          const optionIndex = optionGroup.findIndex(
            (c) => c.id === optionSelected.id
          )

          const updatedOptionGroup = [...optionGroup]
          const filteredOptionGroup = updatedOptionGroup.filter(
            (_, i) => i !== optionIndex
          )

          setOptionGroup(filteredOptionGroup)
        }
      })
    } catch (err) {
      const message =
        err?.response?.data?.error || 'Por Favor entrar em contato com suporte.'

      showToast({ message, type: 'error' })
    }
  }, [
    handleMenuOptionsClose,
    optionSelected,
    optionGroup,
    setOptionGroup,
    showToast
  ])

  return (
    <>
      <StyledMenu>
        <StyledMenuItem onClick={handleModalOptionEdit}>Editar</StyledMenuItem>
        <StyledMenuItem onClick={handleOptionDelete}>Excluir</StyledMenuItem>
      </StyledMenu>

      <OptionModal
        isOpen={isOpen}
        handleToggle={handleToggle}
        optionGroup={optionGroup}
        setOptionGroup={setOptionGroup}
        option={optionSelected}
      />

      <S.OptionsButtons>
        <S.ButtonAdd type="button" onClick={() => handleToggle(null)}>
          <FiPlus /> Criar opção
        </S.ButtonAdd>
      </S.OptionsButtons>

      <S.Container
        handle=".handle"
        animation={150}
        list={optionGroup}
        setList={(newState: OptionGroupProps[]) => setOptionGroup(newState)}
      >
        {optionGroup.map((option, index) => (
          <S.Option key={index}>
            <S.OptionHeader key={option.id}>
              <S.OptionHeaderLeft>
                <button type="button" className="handle">
                  <FaArrowsAlt />
                </button>

                <div>
                  <h1>{option.name}</h1>
                </div>
              </S.OptionHeaderLeft>

              <S.OptionHeaderRight>
                <S.OptionHeaderStatus
                  onChange={() => handleUpdateStatusOption(option.id)}
                  checked={option.available}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  height={20}
                  width={60}
                  handleDiameter={12}
                  offColor={themeContext.colors.red}
                  onColor={themeContext.colors.green}
                />

                <button
                  type="button"
                  title="Opções"
                  onClick={(e) => handleMenuOptionsOpen(e, option)}
                >
                  <FiMoreVertical />
                </button>
              </S.OptionHeaderRight>
            </S.OptionHeader>
          </S.Option>
        ))}
      </S.Container>
    </>
  )
}

export default Options
